top-forms-focusable top-size_s  top-button-withoutText  top-tagSelector-useTopButton

<script setup lang="ts">
import type { Props, TagSelectorTarget } from './types';
import TopPopupOpener from '@/components/popup/popup/opener/opener.vue';
import TopButton from '@/components/forms/button/button.vue';
import { genTagIdClear, getTagById } from '../utils/utils';
import TopTagIcon from '../tagIcon/tagIcon.vue';

defineOptions({
	inheritAttrs: false,
});

const props = defineProps<Props>();

const model = defineModel<Props['modelValue']>({
	required: true,
});

const component = props.useTopButton ? TopButton : 'div';
const componentSlotName = props.useTopButton ? 'html' : 'default';

const topTagSelectorTarget: TagSelectorTarget = {
	model,
	mode: props.mode,
	targetId: props.targetId,
	filters: props.filters,
	payload: props.payload,
};
</script>

<template>
	<TopPopupOpener :id>
		<component
			:is="component"
			:class="{
				'top-tagSelector': true,
				'top-tagSelector-useTopButton': props.useTopButton,
				'top-tagSelector-custom': !props.useTopButton,
				'top-as-selector': props.useTopButton,
				'top-tagSelector-filter': props.mode === 'filter',
				'top-tagSelector-setter_single': props.mode === 'setter' && !filters,
				'top-tagSelector-setter_several': props.mode === 'setter' && filters,
				'top-tagSelector-selectedOne': !model.length || model.length === 1,
				'top-tagSelector-toTwoLine': model.length > 5,
			}"
			color="theme"
			:styling
			v-top-data:topTagSelectorTarget="topTagSelectorTarget"
			:=$attrs
		>
			<template #[componentSlotName]>
				<TopTagIcon
					v-if="!model.length && mode === 'filter'"
					id="all"
					colorId=""
					:name="$i18n.Common.All_tags ?? ''"
					state=""
				/>

				<!-- Массовое редактирование -->
				<div v-if="mode === 'setter' && filters">
					<slot></slot>
				</div>

				<!-- Список тегов -->
				<TopTagIcon
					v-else
					v-for="tagId in model"
					:id="genTagIdClear(tagId)"
					:colorId="getTagById(tagId, tags)?.color_id ?? ''"
					:name="getTagById(tagId, tags)?.name ?? ''"
					:state="genTagIdClear(tagId) === tagId ? 'selected' : 'excluded'"
				/>
			</template>
		</component>
	</TopPopupOpener>
</template>

<style>
.top-tagSelector {
	cursor: pointer;
	min-width: 0;
	white-space: nowrap;
	justify-content: left;
}

.top-tagSelector-useTopButton {
	width: 112px;
	gap: var(--top-gap-1);
	vertical-align: middle;
}

.top-tagSelector.top-button.top-as-selector {
	width: calc(112px + 8px);
}

/* все теги в фильтре */
.top-tagSelector-useTopButton [data-tag_id="all"]:before {
	margin-right: 6px;
}

/* фильтр по одному тегу */
.top-tagSelector-useTopButton.top-tagSelector-selectedOne [data-tag_id]:after {
	content: attr(title);
	margin-left: 20px;
	min-width: 74px;
	text-align: left;
	line-height: 1.2;
	text-overflow: ellipsis;
	overflow: hidden;
}

.top-tagSelector-useTopButton.top-tagSelector-selectedOne [data-tag_id="all"]:after {
	margin-left: 0;
}

/* фильтр по многим тегам */
.top-button.top-tagSelector-useTopButton.top-tagSelector-toTwoLine {
	flex-wrap: wrap;
	align-content: center;
}

.top-button.top-tagSelector-useTopButton.top-tagSelector-toTwoLine [data-tag_id] {
	--top-tag-selector-size: 8px;
}

.top-button.top-tagSelector-useTopButton.top-tagSelector-toTwoLine [data-tag_id]:nth-child(5) {
	margin-right: 4px;
}

/* установка тегов */
.top-tagSelector-useTopButton.top-tagSelector-setter_several,
.top-tagSelector-useTopButton.top-tagSelector-setter_several.top-as-selector{
	width: auto;
}

.top-tagSelector-custom {
	position: relative;
	display: flex !important; flex-direction: column; flex-wrap: wrap; align-items: stretch !important;
}

.top-tagSelector-custom [data-tag_id] {
	width: auto; min-height: 20%; min-width: 50%;
	flex: 1 1 auto;
}
</style>
